import clsx from 'clsx';

import EditableField from '~components/generic/editable-field/EditableField/EditableField';
import { ModuleAttachments } from '~components/generic/module-attachments/ModuleAttachments';
import { IEntityComponentProps } from '~interfaces/props';
import { initialLayout } from '~modules/moodboard/CustomMoodboard/config';
import CustomMoodboard from '~modules/moodboard/CustomMoodboard/CustomMoodboard';
import MoodboardGrid3 from '~modules/moodboard/MoodboardGrid3';
import MoodboardGrid4 from '~modules/moodboard/MoodboardGrid4';
import MoodboardGrid5 from '~modules/moodboard/MoodboardGrid5';
import { getFieldValue, getRelatedEntityIds } from '~services/entity-helpers';
import { getModuleUrlIdFromData } from '~services/location-helpers';
import log from '~services/log';

interface GridItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  aspectRatio?: string;
  id?: string;
}

export interface IMoodboardGridProps {
  stable: boolean;
  editing: boolean;
  data: string[];
  parentContext: string;
  parentType: string;
  fieldName: string;
  siblingsLength: number;
  index: number;
  canMove: boolean;
  gridData?: GridItem[];
  onChangeGridData?: (data: GridItem[]) => void;
  origData: string;
  onAddNewChild: (payload: AddChildPayload) => void;
  onDeleteChild: (payload: DeleteChildPayload) => void;
  id?: string;
}

export const ModuleMoodboard = ({
  data,
  editing,
  index,
  className,
  onChange,
  stable,
  onAddNewChild,
  onDeleteChild,
  origData,
}: IEntityComponentProps) => {
  const childModuleIds = getRelatedEntityIds(data, 'field_images');
  const childModuleIdsLength = childModuleIds.length - 1;
  const gridJsonData = getFieldValue(data, 'attributes.field_grid_json_data');
  const parsedGridData = JSON.parse(gridJsonData as string);

  if (!data) {
    return null;
  }

  const content = data.attributes;
  const moduleClasses = clsx({
    ...className.module,
    relative: true,
  });
  const containerClasses = {
    ...className.container,
    'px-0': true,
    relative: true,
  };

  // starts at 1
  const layoutType: number = content.field_layout;
  // index starts at 0
  const layoutMap = [
    {
      items: 5,
      component: MoodboardGrid5,
    },
    {
      items: 4,
      component: MoodboardGrid4,
    },
    {
      items: 3,
      component: MoodboardGrid3,
    },
    {
      items: 6,
      component: CustomMoodboard,
    },
  ];
  const layoutConfig = layoutMap[layoutType - 1];
  if (!layoutConfig) {
    log(`No layout configuration found for moodboard layout type ${layoutType}!`, null, 'error');
    return null;
  }

  const onChangeGridData = data => {
    onChange('attributes.field_grid_json_data', JSON.stringify(data));
  };

  const MoodboardGrid = layoutConfig.component;
  return (
    <div className={moduleClasses} id={getModuleUrlIdFromData(data.id, data.type)}>
      <div className={clsx(containerClasses)}>
        <div className="c-module__header">
          <EditableField
            hide={content.field_hide_title}
            entityType={data.type}
            fieldName="attributes.field_title.value"
            data={content.field_title.value}
            editing={editing}
            onChange={onChange}
          />
          <ModuleAttachments data={data} />
        </div>
        <div className="c-module__content">
          <div className="u-margin-top-md">
            <MoodboardGrid
              onAddNewChild={onAddNewChild}
              onDeleteChild={onDeleteChild}
              origData={origData}
              gridData={gridJsonData ? parsedGridData : initialLayout}
              data={childModuleIds}
              editing={editing}
              parentContext={data.id}
              parentType={data.type}
              fieldName="relationships.field_images.data"
              siblingsLength={childModuleIdsLength}
              index={index}
              canMove={editing}
              stable={stable}
              onChangeGridData={onChangeGridData}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleMoodboard;
